import type { FormRulesStructure } from '../model/form-rules.types';
import type { FormElement, FormStructure, TableElement } from '../model/form.types';
import type { FieldValue } from '../model/types';
import { isTableElement, migratedElements, snapshotFields } from './field-walk';
import { modelRowColumns } from './model-row';
import { getFieldDefinition } from './registry';
import { evaluateRules } from './rules';
import { isCoveredCell } from './table-cell';

export interface ChangeSlotColumn {
  columnIndex: number;
  fieldId: string;
}

export interface ChangeSlotTable {
  tableId: string;
  columns: ChangeSlotColumn[];
}

export interface ChangeSlots {
  fieldIds: string[];
  tables: ChangeSlotTable[];
}

function ownedTableSlot(table: TableElement): ChangeSlotTable {
  const columns = modelRowColumns(table).flatMap((column) =>
    column.element && column.definition ? [{ columnIndex: column.columnIndex, fieldId: column.element.id }] : [],
  );
  return { tableId: table.id, columns };
}

function collectSlots(elements: FormElement[], drawn: boolean, owners: Set<string>, slots: ChangeSlots): void {
  for (const element of elements) {
    if (isTableElement(element)) collectTableSlots(element, drawn, owners, slots);
    else if (drawn && getFieldDefinition(element)) slots.fieldIds.push(element.id);
  }
}

function collectTableSlots(table: TableElement, drawn: boolean, owners: Set<string>, slots: ChangeSlots): void {
  // Compile claims the rows of a duplicated id for the first multiple table in this same
  // pre-order, a covered one included, so a covered owner still leaves its twin without rows.
  if (table.properties.is_multiple === true && !owners.has(table.id)) {
    owners.add(table.id);
    if (drawn) slots.tables.push(ownedTableSlot(table));
  }
  for (const cell of (table.properties.cells ?? []).flat()) {
    if (cell.content) collectSlots([cell.content], drawn && !isCoveredCell(cell), owners, slots);
  }
}

// Where a change mark can show on the sheet: every drawn field, once per place it is drawn, and the
// multiple tables that own added rows, with the field of each model row column.
export function listChangeSlots(structure: FormStructure): ChangeSlots {
  const slots: ChangeSlots = { fieldIds: [], tables: [] };
  collectSlots(migratedElements(structure), true, new Set(), slots);
  return slots;
}

export interface VisibilityTimelineInput {
  matrixId: number;
  structure: FormStructure;
  rules: FormRulesStructure | null;
}

export interface VisibilityTimelineResponse {
  changes: { values: Record<string, FieldValue> };
}

// The rules read only the field values, never a table row, so the fold keeps one values object:
// compiling a photo per response copies every row into each one and grows with responses times rows.
// The visibility of an added row cell is the one of its model row field, by the same id.
export function* visibilityTimeline(
  input: VisibilityTimelineInput,
  responses: readonly VisibilityTimelineResponse[],
): Generator<(fieldId: string) => boolean> {
  const elements = migratedElements(input.structure);
  const values: Record<string, FieldValue> = {};

  for (const response of responses) {
    Object.assign(values, response.changes.values);
    const answers = { version: 1 as const, matrixId: input.matrixId, values, tables: {} };
    const rule = evaluateRules(input.rules, snapshotFields(elements, answers));
    yield (fieldId) => rule(fieldId).visible;
  }
}
